import React, { useCallback, useEffect, useState } from 'react'
import { useNotification } from '../../hooks/useNotification'
import services from '../../services'
import './avisoGuiaNovo.scss'

export type ChaveDoGuia = 'relatorios' | 'emails' | 'templates'

interface StatusDoGuia {
  guia: string
  nome: string
  versaoAtual: string
  versaoBaixada?: string | null
  desatualizado: boolean
}

/** Quem baixa um guia por um botão da tela avisa o aviso (se estiver aberto) para ele sumir sem recarregar a página. */
const EVENTO = 'np-guia-baixado'
export const avisarGuiaBaixado = () => window.dispatchEvent(new Event(EVENTO))

function baixarTexto(nomeArquivo: string, conteudo: string) {
  const url = URL.createObjectURL(new Blob([conteudo], { type: 'text/markdown;charset=utf-8' }))
  const link = document.createElement('a')
  link.href = url
  link.download = nomeArquivo
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/**
 * Avisa quem edita relatórios que existe uma versão nova do guia do Claude que a pessoa ainda não baixou (ou que nunca baixou).
 * O servidor anota a versão quando o guia é baixado pelo portal; sem baixar de novo, o Claude gera com as regras antigas.
 * Nunca atrapalha a tela: se não conseguir saber o status, não mostra nada.
 */
export default function AvisoGuiaNovo({ guia, dashboardId }: { guia: ChaveDoGuia; dashboardId?: number }) {
  const { notify } = useNotification()
  const [status, setStatus] = useState<StatusDoGuia | null>(null)
  const [baixando, setBaixando] = useState(false)

  const carregar = useCallback(async () => {
    try {
      const r: any = await services.api.guiasClaudeStatus()
      if (r?.fail || !Array.isArray(r?.data)) { setStatus(null); return }
      setStatus((r.data as StatusDoGuia[]).find(g => g.guia === guia) ?? null)
    } catch {
      setStatus(null)
    }
  }, [guia])

  useEffect(() => {
    carregar()
    window.addEventListener(EVENTO, carregar)
    return () => window.removeEventListener(EVENTO, carregar)
  }, [carregar])

  const baixar = async () => {
    setBaixando(true)
    try {
      const r: any =
        guia === 'relatorios' ? await services.api.relatorioHtmlGuiaClaude()
          : guia === 'templates' ? await services.api.templatesHtmlGuiaClaude()
            : await services.api.disparosEmailGuia(dashboardId)
      if (r.fail) { notify(r.error || 'Não foi possível baixar o guia.', { variant: 'error' }); return }
      baixarTexto(r.data.nomeArquivo, r.data.conteudo)
      avisarGuiaBaixado()
    } finally {
      setBaixando(false)
    }
  }

  if (!status?.desatualizado) return null

  return (
    <div className="agn" role="status" data-testid="aviso-guia-novo">
      <div className="agn-texto">
        {status.versaoBaixada ? (
          <>
            <b>Tem uma versão nova do {status.nome} ({status.versaoAtual}).</b> A que você baixou foi a {status.versaoBaixada}.
            Baixe e troque o arquivo no projeto do Claude: senão ele continua gerando com as regras antigas.
          </>
        ) : (
          <>
            <b>Você ainda não baixou o {status.nome}.</b> Baixe e coloque o arquivo no projeto do Claude: é ele que ensina o Claude as
            regras do portal.
          </>
        )}
      </div>
      <button type="button" className="ng-btn prim" disabled={baixando} onClick={baixar}>Baixar guia</button>
    </div>
  )
}
